/**
 * Klasse SCHREIBEN_CLICK_NOT_CHAT — Schreiben aus der sichtbaren Akte,
 * ohne ARKO-/Composer-Pflicht.
 *
 * Polar:
 *   VERBOTEN: Export ohne sichtbare akte_id, stilles Pool-Dokument oder
 *   vorgetäuschter juristischer Volltext.
 *   ERLAUBT: Anwender wählt ein neutrales Gerüst, trägt den Inhalt selbst ein
 *   und exportiert erst nach bestätigter Aktbindung.
 */

export const PORTAL_AKTE_SCHREIBEN_CLASS = 'SCHREIBEN_CLICK_NOT_CHAT' as const;

export const PORTAL_AKTE_SCHREIBEN_TEMPLATES = [
  {
    id: 'kanzleischreiben',
    label: 'Allgemeines Kanzleischreiben',
    hint: 'Neutrales Briefgerüst für einen selbst verfassten Inhalt.',
    heading: '',
  },
  {
    id: 'stellungnahme',
    label: 'Stellungnahme',
    hint: 'Struktur für eine selbst formulierte Stellungnahme.',
    heading: 'STELLUNGNAHME',
  },
  {
    id: 'begleitschreiben',
    label: 'Begleitschreiben',
    hint: 'Kurzes Gerüst für die Übermittlung von Unterlagen.',
    heading: 'BEGLEITSCHREIBEN',
  },
] as const;

export type PortalAkteSchreibenTemplateId =
  (typeof PORTAL_AKTE_SCHREIBEN_TEMPLATES)[number]['id'];

export type PortalAkteSchreibenFormat = 'docx' | 'pdf';

export type PortalAkteSchreibenDraftInput = {
  akteId?: string | null;
  templateId: PortalAkteSchreibenTemplateId;
  recipient: string;
  address?: string;
  subject: string;
  body: string;
  kanzleiName?: string;
};

export type PortalAkteSchreibenValidation =
  | { ok: true }
  | { ok: false; error: string };

export function getPortalAkteSchreibenTemplate(
  id: string,
) {
  return (
    PORTAL_AKTE_SCHREIBEN_TEMPLATES.find((template) => template.id === id) ??
    PORTAL_AKTE_SCHREIBEN_TEMPLATES[0]
  );
}

/** Kein Write ohne explizite sichtbare Akte und selbst eingegebenen Kerninhalt. */
export function validatePortalAkteSchreibenDraft(
  input: PortalAkteSchreibenDraftInput,
): PortalAkteSchreibenValidation {
  if (!input.akteId?.trim()) {
    return { ok: false, error: 'Keine sichtbare Akte — Schreiben wurde nicht erzeugt.' };
  }
  if (!PORTAL_AKTE_SCHREIBEN_TEMPLATES.some((template) => template.id === input.templateId)) {
    return { ok: false, error: 'Schreiben-Vorlage ist nicht verfügbar.' };
  }
  if (!input.recipient.trim()) {
    return { ok: false, error: 'Empfänger fehlt.' };
  }
  if (!input.subject.trim()) {
    return { ok: false, error: 'Betreff fehlt.' };
  }
  if (input.body.trim().length < 10) {
    return { ok: false, error: 'Bitte den Inhalt selbst ausformulieren (mindestens 10 Zeichen).' };
  }
  return { ok: true };
}

export function buildPortalAkteSchreibenText(
  input: PortalAkteSchreibenDraftInput,
  now = new Date(),
): string {
  const validation = validatePortalAkteSchreibenDraft(input);
  if (validation.ok === false) throw new Error(validation.error);

  const template = getPortalAkteSchreibenTemplate(input.templateId);
  const date = new Intl.DateTimeFormat('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(now);

  return [
    input.recipient.trim(),
    input.address?.trim() || '',
    '',
    date,
    '',
    template.heading,
    template.heading ? '' : null,
    `Betreff: ${input.subject.trim()}`,
    '',
    'Sehr geehrte Damen und Herren,',
    '',
    input.body.trim(),
    '',
    'Mit freundlichen Grüßen',
    input.kanzleiName?.trim() || '[Kanzlei]',
    '',
    '[Entwurf – anwaltlich zu prüfen]',
  ]
    .filter((line): line is string => line !== null)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function safeFileStem(value: string): string {
  return value
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/g, '')
    .trim()
    .slice(0, 120);
}

export function portalAkteSchreibenDisplayName(
  input: Pick<PortalAkteSchreibenDraftInput, 'templateId' | 'subject'>,
  format: PortalAkteSchreibenFormat,
): string {
  const template = getPortalAkteSchreibenTemplate(input.templateId);
  const stem =
    safeFileStem(`${template.label} - ${input.subject}`) ||
    safeFileStem(template.label) ||
    'Schreiben';
  return `${stem}.${format}`;
}

/**
 * Generate-GET reicht nicht: die sichtbare Akt-Liste muss dieselbe PB-Zeile
 * mit derselben akte_id enthalten (Refresh darf sie nicht verwerfen).
 */
export function portalConfirmedAkteDocVisibleInList(
  list: Array<{ id?: string | null; akte_id?: string | null }>,
  confirmedId: string,
  akteId: string,
): boolean {
  const id = confirmedId.trim();
  const aid = akteId.trim();
  if (!id || !aid) return false;
  return list.some(
    (row) => row.id?.trim() === id && (row.akte_id || '').trim() === aid,
  );
}
